import { useState, useEffect, useMemo } from 'react';
import api from '../api/axios';

const STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Deleted' },
  { value: 'rejected', label: 'Rejected' },
];
const STATUS_RANK = { pending: 0, completed: 1, rejected: 2 };

const STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-600',
  completed: 'bg-[#ECFDF5] text-[#10B981]',
  rejected: 'bg-[#FEF2F2] text-[#EF4444]',
};
const STATUS_LABELS = { pending: 'Pending', completed: 'Deleted', rejected: 'Rejected' };

const BASE_TYPES = ['job_seeker', 'employer'];
const typeLabel = (t) => (t || '').replace(/_/g, ' ');
const typeTone = (t) =>
  t === 'employer'
    ? 'bg-indigo-50 text-indigo-600 border-indigo-100'
    : t === 'job_seeker'
      ? 'bg-purple-50 text-purple-600 border-purple-100'
      : 'bg-gray-100 text-gray-600 border-gray-200';

const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

const waLink = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return null;
  return `https://wa.me/${digits.length === 10 ? '91' + digits : digits}`;
};

const FIELD = 'w-full border border-gray-200 rounded-full px-4 py-2 text-sm bg-white text-gray-700 hover:border-purple-300 transition focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent';
const SELECT = `${FIELD} appearance-none cursor-pointer pr-9`;
const CAPTION = 'block text-xs font-semibold text-gray-500 mb-1 ml-1';
const ACT = 'inline-flex items-center justify-center gap-1 h-8 px-3 rounded-full text-xs font-semibold border shadow-sm transition whitespace-nowrap disabled:opacity-40';

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const presetRange = (days) => {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - (days - 1));
  return { from: iso(from), to: iso(to) };
};
const DATE_PRESETS = [
  { label: 'Today', days: 1 },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
];

const compareSort = (av, bv, dir) => {
  const aEmpty = av === null || av === undefined || av === '';
  const bEmpty = bv === null || bv === undefined || bv === '';
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  const r =
    typeof av === 'number' && typeof bv === 'number'
      ? av - bv
      : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
  return dir === 'asc' ? r : -r;
};

function SortTh({ label, sortKey, sort, onSort }) {
  const active = sort.key === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      title="Click to sort"
      className={`text-left px-4 py-3 font-semibold cursor-pointer select-none whitespace-nowrap transition hover:text-[#DB2777] ${active ? 'text-[#DB2777]' : ''}`}
    >
      {label}
      <span className={`ml-1 ${active ? '' : 'opacity-40'}`}>{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
    </th>
  );
}

function SelectBox({ caption, value, onChange, children }) {
  return (
    <div>
      <label className={CAPTION}>{caption}</label>
      <div className="relative">
        <select value={value} onChange={onChange} className={SELECT}>{children}</select>
        <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-purple-500 text-[10px]">▼</span>
      </div>
    </div>
  );
}

function Avatar({ name }) {
  return (
    <div className="w-9 h-9 shrink-0 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#DB2777] text-white flex items-center justify-center text-sm font-bold">
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
}

function Pager({ page, lastPage, onPage }) {
  const nums = [];
  for (let p = Math.max(1, page - 2); p <= Math.min(lastPage, page + 2); p++) nums.push(p);
  const btn = 'px-3 py-1.5 rounded-lg text-xs font-semibold border transition disabled:opacity-40 disabled:cursor-not-allowed';
  return (
    <div className="flex items-center gap-1">
      <button className={`${btn} bg-white hover:bg-purple-50`} disabled={page <= 1} onClick={() => onPage(1)}>«</button>
      <button className={`${btn} bg-white hover:bg-purple-50`} disabled={page <= 1} onClick={() => onPage(page - 1)}>Prev</button>
      {nums.map((p) => (
        <button
          key={p}
          onClick={() => onPage(p)}
          className={`${btn} ${p === page ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm' : 'bg-white hover:bg-purple-50'}`}
        >
          {p}
        </button>
      ))}
      <button className={`${btn} bg-white hover:bg-purple-50`} disabled={page >= lastPage} onClick={() => onPage(page + 1)}>Next</button>
      <button className={`${btn} bg-white hover:bg-purple-50`} disabled={page >= lastPage} onClick={() => onPage(lastPage)}>»</button>
    </div>
  );
}

function EmptyState({ icon, title, hint }) {
  return (
    <div className="py-14 text-center">
      <div className="mx-auto w-14 h-14 rounded-full bg-purple-50 flex items-center justify-center text-2xl">{icon}</div>
      <p className="mt-3 font-semibold text-gray-700">{title}</p>
      {hint && <p className="text-sm text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function SkeletonRows({ cols, rows = 5 }) {
  return Array.from({ length: rows }).map((_, i) => (
    <tr key={i} className="border-t border-gray-50">
      {Array.from({ length: cols }).map((__, c) => (
        <td key={c} className="px-4 py-4"><div className="h-4 rounded-full bg-gray-100 animate-pulse" style={{ width: `${55 + ((i + c) % 4) * 10}%` }} /></td>
      ))}
    </tr>
  ));
}

function DeletionRequestsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState({ key: 'requested', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [busyId, setBusyId] = useState(null);
  const [reasonRow, setReasonRow] = useState(null);

  // Loads every request once; status, type, date and search are filtered here
  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/deletion-requests');
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load deletion requests. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [search, status, typeFilter, dateFrom, dateTo, sort, pageSize]);

  // Deletes the account with the SAME admin endpoint the Users tab uses,
  // then marks the request as completed.
  const approveAndDelete = async (r) => {
    if (!window.confirm(`Permanently delete the account of "${r.name}"? This cannot be undone.`)) return;
    setBusyId(r.id);
    try {
      if (r.user_id) {
        try {
          await api.delete(`/admin/users/${r.user_id}`);
        } catch (err) {
          // 404 = account already gone, so just close the request
          if (err.response?.status !== 404) throw err;
        }
      }
      await api.put(`/admin/deletion-requests/${r.id}`, { status: 'completed' });
      setReasonRow(null);
      await load();
    } catch (err) {
      alert(err.response?.data?.message || 'Could not delete the account. The request is still pending.');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (r) => {
    const note = window.prompt('Reason for rejecting (shown to the user):', '');
    if (note === null) return;
    setBusyId(r.id);
    try {
      await api.put(`/admin/deletion-requests/${r.id}`, { status: 'rejected', admin_note: note || null });
      setReasonRow(null);
      await load();
    } catch (err) {
      alert(err.response?.data?.message || 'Could not reject the request. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  const onSort = (key) =>
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }));

  const typeOptions = useMemo(
    () => Array.from(new Set([...BASE_TYPES, ...rows.map((r) => r.role).filter(Boolean)])),
    [rows]
  );

  // Everything except status, so the status tabs show their own counts
  const baseFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (typeFilter && r.role !== typeFilter) return false;
      if (dateFrom || dateTo) {
        if (!r.created_at) return false;
        const d = new Date(r.created_at).setHours(0, 0, 0, 0);
        if (dateFrom && d < new Date(dateFrom).setHours(0, 0, 0, 0)) return false;
        if (dateTo && d > new Date(dateTo).setHours(23, 59, 59, 999)) return false;
      }
      if (!q) return true;
      return (
        r.name?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        r.phone?.includes(search.trim()) ||
        r.reason?.toLowerCase().includes(q)
      );
    });
  }, [rows, search, typeFilter, dateFrom, dateTo]);

  const statusCounts = useMemo(() => {
    const c = { pending: 0, completed: 0, rejected: 0 };
    baseFiltered.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [baseFiltered]);

  const filtered = useMemo(
    () => (status ? baseFiltered.filter((r) => r.status === status) : baseFiltered),
    [baseFiltered, status]
  );

  const sortValue = (r, key) => {
    switch (key) {
      case 'name': return r.name || '';
      case 'email': return r.email || '';
      case 'phone': return r.phone || '';
      case 'type': return typeLabel(r.role);
      case 'requested': return r.created_at ? new Date(r.created_at).getTime() : null;
      case 'reason': return r.reason || '';
      case 'status': return STATUS_RANK[r.status] ?? 9;
      case 'actions': return r.processed_at ? new Date(r.processed_at).getTime() : null;
      default: return null;
    }
  };

  const sorted = useMemo(
    () => filtered.slice().sort((a, b) => compareSort(sortValue(a, sort.key), sortValue(b, sort.key), sort.dir)),
    [filtered, sort]
  );

  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const cur = Math.min(page, pages);
  const paged = sorted.slice((cur - 1) * pageSize, cur * pageSize);

  const totals = {
    all: rows.length,
    pending: rows.filter((r) => r.status === 'pending').length,
    completed: rows.filter((r) => r.status === 'completed').length,
    rejected: rows.filter((r) => r.status === 'rejected').length,
  };

  const clearFilters = () => { setSearch(''); setStatus(''); setTypeFilter(''); setDateFrom(''); setDateTo(''); };

  const chips = [
    search && { k: 'q', text: `Search: ${search}`, clear: () => setSearch('') },
    status && { k: 's', text: STATUS_LABELS[status], clear: () => setStatus('') },
    typeFilter && { k: 't', text: typeLabel(typeFilter), clear: () => setTypeFilter('') },
    dateFrom && { k: 'df', text: `From ${dateFrom}`, clear: () => setDateFrom('') },
    dateTo && { k: 'dt', text: `To ${dateTo}`, clear: () => setDateTo('') },
  ].filter(Boolean);

  const activePreset = DATE_PRESETS.find((p) => {
    const r = presetRange(p.days);
    return dateFrom === r.from && dateTo === r.to;
  })?.days;

  const cards = [
    { title: 'All requests', value: totals.all, icon: '🗂️', tone: 'from-[#7C3AED] to-[#A78BFA]', status: '' },
    { title: 'Pending', value: totals.pending, icon: '⏳', tone: 'from-[#F59E0B] to-[#FBBF24]', status: 'pending' },
    { title: 'Deleted', value: totals.completed, icon: '🗑️', tone: 'from-[#10B981] to-[#34D399]', status: 'completed' },
    { title: 'Rejected', value: totals.rejected, icon: '🚫', tone: 'from-[#EF4444] to-[#F87171]', status: 'rejected' },
  ];

  const tabs = [
    { value: 'pending', label: 'Pending', count: statusCounts.pending },
    { value: 'completed', label: 'Deleted', count: statusCounts.completed },
    { value: 'rejected', label: 'Rejected', count: statusCounts.rejected },
    { value: '', label: 'All', count: baseFiltered.length },
  ];

  const cols = [
    ['Name', 'name'], ['Email', 'email'], ['Phone', 'phone'], ['Type', 'type'],
    ['Requested', 'requested'], ['Reason', 'reason'], ['Status', 'status'],
  ];

  return (
    <div className="space-y-5">
      {/* Summary (click a card to filter by status) */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {cards.map((c) => (
          <button
            key={c.title}
            type="button"
            onClick={() => setStatus(c.status)}
            className={`text-left bg-white rounded-2xl shadow-md shadow-purple-100/60 border p-5 flex items-center gap-4 transition hover:-translate-y-0.5 hover:shadow-lg ${
              status === c.status ? 'border-purple-300 ring-2 ring-purple-200' : 'border-purple-50'
            }`}
          >
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.tone} text-white flex items-center justify-center text-xl shadow-sm shrink-0`}>{c.icon}</div>
            <div>
              <p className="text-2xl font-extrabold text-[#0D1B3D] leading-tight">{c.value}</p>
              <p className="text-sm font-semibold text-gray-600">{c.title}</p>
            </div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[#0D1B3D]">Filter requests</h3>
          {chips.length > 0 && (
            <button type="button" onClick={clearFilters}
              className="text-xs font-semibold px-3 py-1.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 transition">
              ✕ Clear all
            </button>
          )}
        </div>

        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm pointer-events-none">🔍</span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, phone or reason"
            className={`${FIELD} pl-10`}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <SelectBox caption="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </SelectBox>
          <SelectBox caption="Account type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">All types</option>
            {typeOptions.map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
          </SelectBox>
          <div className="flex flex-wrap gap-2 items-end pb-0.5">
            {DATE_PRESETS.map((p) => (
              <button key={p.days} type="button"
                onClick={() => { const r = presetRange(p.days); setDateFrom(r.from); setDateTo(r.to); }}
                className={`px-3.5 py-2 rounded-full text-xs font-semibold border transition ${
                  activePreset === p.days
                    ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent shadow-sm'
                    : 'bg-white text-purple-700 border-purple-200 hover:border-purple-400'
                }`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className={CAPTION}>Requested from</label>
            <input type="date" className={`${FIELD} w-44`} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className={CAPTION}>Requested to</label>
            <input type="date" className={`${FIELD} w-44`} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </div>

        {chips.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {chips.map((c) => (
              <span key={c.k} className="inline-flex items-center gap-1.5 text-xs font-medium pl-3 pr-1.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-100">
                {c.text}
                <button type="button" onClick={c.clear} className="h-4 w-4 rounded-full hover:bg-purple-200 leading-none" aria-label="Remove filter">×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {error && !loading && (
        <div className="flex items-center justify-between gap-3 bg-red-50 border border-red-100 text-red-700 rounded-xl px-4 py-3 text-sm">
          <span>{error}</span>
          <button type="button" onClick={load} className="font-semibold underline">Retry</button>
        </div>
      )}

      {/* Status tabs */}
      <div className="flex gap-1 border-b border-gray-100 overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.label}
            type="button"
            onClick={() => setStatus(t.value)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition inline-flex items-center gap-2 whitespace-nowrap ${
              status === t.value ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            {t.label}
            <span className={`text-[11px] font-bold h-5 min-w-[1.25rem] px-1.5 rounded-full flex items-center justify-center ${
              status === t.value ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-500'
            }`}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-md shadow-purple-100/60 border border-purple-50 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 text-xs text-gray-400">
          Showing <span className="font-semibold text-purple-600">{sorted.length}</span> of {rows.length} requests. Click a column heading to sort.
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1150px]">
            <thead className="bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 text-[11px] tracking-wider uppercase border-b border-gray-100">
              <tr>
                {cols.map(([h, k]) => <SortTh key={k} label={h} sortKey={k} sort={sort} onSort={onSort} />)}
                <SortTh label="Actions" sortKey="actions" sort={sort} onSort={onSort} />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonRows cols={8} />
              ) : paged.length === 0 ? (
                <tr><td colSpan={8}>
                  <EmptyState
                    icon={rows.length === 0 ? '📭' : '🔎'}
                    title={rows.length === 0 ? 'No deletion requests yet' : 'No requests match your filters'}
                    hint={rows.length === 0 ? 'Account deletion requests from users will appear here.' : 'Clear a filter or widen the date range.'}
                  />
                </td></tr>
              ) : (
                paged.map((r) => {
                  const wa = waLink(r.phone);
                  return (
                    <tr key={r.id} className="border-t border-gray-50 hover:bg-purple-50/60 transition-colors align-top">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar name={r.name} />
                          <span className="font-semibold text-gray-800">{r.name || '—'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 max-w-[200px] truncate" title={r.email}>{r.email || '—'}</td>
                      <td className="px-4 py-3">
                        {r.phone ? (
                          <div className="space-y-1.5">
                            <p className="tabular-nums text-gray-700">{r.phone}</p>
                            <div className="flex gap-1.5">
                              <a href={`tel:${r.phone}`} className="inline-flex items-center h-6 px-2.5 rounded-full text-[11px] font-semibold border bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100">📞 Call</a>
                              {wa && <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center h-6 px-2.5 rounded-full text-[11px] font-semibold border bg-green-50 text-green-600 border-green-100 hover:bg-green-100">💬 WhatsApp</a>}
                            </div>
                          </div>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border capitalize whitespace-nowrap ${typeTone(r.role)}`}>{typeLabel(r.role) || '—'}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(r.created_at)}</td>
                      <td className="px-4 py-3 max-w-[240px]">
                        <p className="text-gray-600 leading-snug break-words" title={r.reason}
                          style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {r.reason || '—'}
                        </p>
                        <button type="button" onClick={() => setReasonRow(r)} className="text-[11px] font-semibold text-purple-600 hover:underline mt-0.5">View full</button>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[r.status] || 'bg-gray-100 text-gray-500'}`}>
                          {STATUS_LABELS[r.status] || r.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {r.status === 'pending' ? (
                          <div className="flex gap-2">
                            <button type="button" onClick={() => approveAndDelete(r)} disabled={busyId === r.id}
                              className={`${ACT} bg-red-50 text-red-500 border-red-100 hover:bg-red-100`}>
                              {busyId === r.id ? 'Working…' : '🗑 Delete account'}
                            </button>
                            <button type="button" onClick={() => reject(r)} disabled={busyId === r.id}
                              className={`${ACT} bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100`}>
                              ✗ Reject
                            </button>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400">
                            <p>{fmtDateTime(r.processed_at)}</p>
                            {r.admin_note && <p className="mt-0.5 text-gray-500 break-words max-w-[200px]">{r.admin_note}</p>}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between flex-wrap gap-3 text-sm text-gray-500">
          <div className="flex items-center gap-3">
            <span>Page {cur} of {pages}</span>
            <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="border border-gray-200 rounded-lg px-2 py-1 text-xs bg-white">
              {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
            </select>
          </div>
          <Pager page={cur} lastPage={pages} onPage={setPage} />
        </div>
      </div>

      {/* Detail popup */}
      {reasonRow && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4" onClick={() => setReasonRow(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[88vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-purple-700 to-pink-600 px-6 py-5 flex items-start justify-between shrink-0">
              <div>
                <h3 className="text-lg font-bold text-white">{reasonRow.name || 'Deletion request'}</h3>
                <p className="text-purple-100 text-xs mt-0.5">{typeLabel(reasonRow.role)} · requested {fmtDateTime(reasonRow.created_at)}</p>
              </div>
              <button onClick={() => setReasonRow(null)} className="text-purple-100 hover:text-white text-2xl leading-none" aria-label="Close">×</button>
            </div>

            <div className="px-6 py-5 overflow-y-auto space-y-4 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-xl bg-gray-50 p-3">
                  <p className="text-xs text-gray-400">Email</p>
                  <p className="text-gray-700 break-all">{reasonRow.email || '—'}</p>
                </div>
                <div className="rounded-xl bg-gray-50 p-3">
                  <p className="text-xs text-gray-400">Phone</p>
                  <p className="text-gray-700 tabular-nums">{reasonRow.phone || '—'}</p>
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-400 mb-1">Reason</p>
                <p className="whitespace-pre-wrap break-words text-gray-700 bg-purple-50/50 border border-purple-100 rounded-xl p-3">{reasonRow.reason || '—'}</p>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">Status</span>
                <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[reasonRow.status] || 'bg-gray-100 text-gray-500'}`}>
                  {STATUS_LABELS[reasonRow.status] || reasonRow.status}
                </span>
              </div>

              {reasonRow.status !== 'pending' && (
                <div className="rounded-xl bg-gray-50 p-3">
                  <p className="text-xs text-gray-400">Processed {fmtDateTime(reasonRow.processed_at)}</p>
                  {reasonRow.admin_note && <p className="text-gray-700 mt-1 break-words">{reasonRow.admin_note}</p>}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {reasonRow.phone && <a href={`tel:${reasonRow.phone}`} className={`${ACT} bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-100`}>📞 Call</a>}
                {waLink(reasonRow.phone) && <a href={waLink(reasonRow.phone)} target="_blank" rel="noreferrer" className={`${ACT} bg-green-50 text-green-600 border-green-100 hover:bg-green-100`}>💬 WhatsApp</a>}
                {reasonRow.email && <a href={`mailto:${reasonRow.email}`} className={`${ACT} bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-100`}>✉️ Email</a>}
              </div>
            </div>

            {reasonRow.status === 'pending' && (
              <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
                <button type="button" onClick={() => reject(reasonRow)} disabled={busyId === reasonRow.id}
                  className={`${ACT} bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100`}>✗ Reject</button>
                <button type="button" onClick={() => approveAndDelete(reasonRow)} disabled={busyId === reasonRow.id}
                  className={`${ACT} bg-red-50 text-red-500 border-red-100 hover:bg-red-100`}>
                  {busyId === reasonRow.id ? 'Working…' : '🗑 Delete account'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default DeletionRequestsPanel;